import React from 'react';
import type { HomepageHeatResponse } from '../../../apiClient';
import RecentMovementCard from './RecentMovementCard';

function countRecent(items: HomepageHeatResponse['items'], eventType?: HomepageHeatResponse['items'][number]['eventType']): number {
  const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return items.filter((item) => item.eventTimestamp >= cutoff && (!eventType || item.eventType === eventType)).length;
}

const HeatNowRail: React.FC<{ heat: HomepageHeatResponse }> = ({ heat }) => {
  const recentUpdates = countRecent(heat.items);
  const evidenceMoves = countRecent(heat.items, 'new_evidence');
  const scoreMoves = countRecent(heat.items, 'score_change');

  return (
    <section className="max-w-6xl mx-auto mb-14">
      <div className="rounded-3xl border border-gold-500/20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-950 text-cream-50 p-5 sm:p-6 shadow-2xl">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-6">
          <div className="max-w-3xl">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gold-400 mb-3">
              Heat now
            </p>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight leading-[0.95] mb-3">
              The accountability desk is moving.
            </h2>
            <p className="text-sm sm:text-base text-cream-200/85 leading-relaxed">
              This rail ranks public officials by the freshest explicit movement signal we have captured:
              new evidence, fresh dossier intelligence, social activity, or a score refresh.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-3 min-w-full lg:min-w-[360px]">
            <MetricCard label="Moves this week" value={String(recentUpdates)} />
            <MetricCard label="New evidence" value={String(evidenceMoves)} />
            <MetricCard label="Score shifts" value={String(scoreMoves)} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          {heat.items.slice(0, 3).map((item) => (
            <RecentMovementCard key={`${item.officialId}-${item.eventTimestamp}`} item={item} />
          ))}
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 pt-4 border-t border-white/10">
          <p className="text-[10px] font-black uppercase tracking-widest text-cream-300/65">
            Snapshot generated {new Date(heat.generatedAt).toLocaleString([], {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
          <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
            {heat.logic.map((line) => (
              <span
                key={line}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[9px] font-black uppercase tracking-[0.18em] text-cream-300/70"
              >
                {line}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const MetricCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-3 sm:p-4">
    <p className="text-[9px] font-black uppercase tracking-[0.22em] text-cream-300/70 mb-1">{label}</p>
    <p className="text-xl sm:text-2xl font-black uppercase tracking-tight text-gold-400">{value}</p>
  </div>
);

export default HeatNowRail;
